/**
 * Chunk que some no deploy: recarregar UMA vez.
 *
 * O ConsoleAI serve o frontend de dentro da imagem, e cada deploy substitui
 * `assets/` inteiro. A aba que já estava aberta guarda em memória o `index` velho
 * e, ao navegar para uma rota com `lazy()`, pede um hash que não existe mais:
 *
 *     GET /assets/QueryRunnerTab-VCTIrUc7.js → 404
 *     Failed to fetch dynamically imported module
 *
 * Os cabeçalhos já estão certos — `index.html` é `no-store` e `assets/*` é
 * `immutable`. Não é cache mal configurado; é a aba estar atrás de um deploy.
 *
 * O `ScreenErrorBoundary` capturava e mostrava a tela de erro, mas o botão
 * "Tentar novamente" só re-renderiza: o navegador guarda a resolução falhada do
 * módulo, então a segunda tentativa falha igual. Só um reload sai disso.
 *
 * **Um reload resolve; dois viram laço**, e laço é pior que a tela de erro —
 * daí a marca em `sessionStorage`. Ela vive só na aba, e some quando a aba fecha.
 */
import { lazy, type ComponentType } from 'react'

export const CHAVE_RELOAD = 'consoleai:chunk-reload'

/** Mensagens dos três motores para a mesma falha. Chrome/Edge dizem "Failed to
 *  fetch dynamically imported module"; Firefox, "error loading dynamically
 *  imported module"; Safari, "Importing a module script failed". */
const PADRAO =
  /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed/i

export function ehErroDeChunk(erro: unknown): boolean {
  const msg = erro instanceof Error ? erro.message : String(erro ?? '')
  return PADRAO.test(msg)
}

/**
 * Recarrega a página uma única vez por aba. Devolve `true` se recarregou.
 *
 * `recarregar` é injetado para o teste não precisar mexer em `window.location`.
 */
export function recarregarUmaVez(erro: unknown, recarregar: () => void): boolean {
  if (!ehErroDeChunk(erro)) return false
  try {
    if (sessionStorage.getItem(CHAVE_RELOAD)) return false
    sessionStorage.setItem(CHAVE_RELOAD, String(Date.now()))
  } catch {
    // Aba anônima ou storage bloqueado. Recarregar uma vez ainda é melhor que a
    // tela de erro, e sem storage não há marca — logo não há laço persistido.
  }
  recarregar()
  return true
}

/** `lazy()` que recarrega a página quando o chunk sumiu no deploy.
 *
 *  A assinatura espelha a do próprio `React.lazy` — estreitar o genérico aqui
 *  faria o componente carregado perder os tipos das próprias props. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyComRetry<T extends ComponentType<any>>(
  fabrica: () => Promise<{ default: T }>,
) {
  return lazy(() =>
    fabrica().catch((erro) => {
      recarregarUmaVez(erro, () => window.location.reload())
      // Re-lança: se o reload não acontecer (já houve um nesta aba), o
      // ScreenErrorBoundary ainda mostra a tela em vez de um branco.
      throw erro
    }),
  )
}
